import {
  Player,
  PlayerWithPerformance,
  PerformanceReport,
  ApiResponse,
  PaginatedResponse,
  PlayerFilters,
  PlayerForm,
} from '@/types';
import api from './client';

// Players API
export const playersAPI = {
  getAll: async (filters?: PlayerFilters & { q?: string; injuryStatus?: 'fit'|'injured'|'recovering'|'questionable'; minRating?: number; maxRating?: number; recentDays?: number; playedLastMatch?: boolean; hasPerformanceData?: boolean; group?: 'squad' | 'external' }, page = 1, limit = 20): Promise<PaginatedResponse<Player>> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.position !== undefined) params.append('position', String(filters.position || ''));
      if (filters.team !== undefined) params.append('team', String(filters.team || ''));
      if (filters.ageRange) {
        if (filters.ageRange.min !== undefined) params.append('min_age', String(filters.ageRange.min));
        if (filters.ageRange.max !== undefined) params.append('max_age', String(filters.ageRange.max));
      }
      if (filters.q) params.append('q', String(filters.q));
      if (filters.injuryStatus) params.append('injury_status', String(filters.injuryStatus));
      if (filters.minRating !== undefined) params.append('min_rating', String(filters.minRating));
      if (filters.maxRating !== undefined) params.append('max_rating', String(filters.maxRating));
      if (filters.recentDays !== undefined) params.append('recent_days', String(filters.recentDays));
      if (filters.playedLastMatch) params.append('played_last_match', '1');
      if (filters.hasPerformanceData !== undefined) {
        params.append('has_performance_data', filters.hasPerformanceData ? '1' : '0');
      }
      if (filters.group) params.append('group', filters.group);
    }
    params.append('page', String(page));
    params.append('limit', String(limit));
    const response = await api.get(`/players?${params.toString()}`);
    return response.data;
  },
  listTeams: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/players/teams');
    return response.data;
  },

  getAllWithPerformance: async (filters?: PlayerFilters & { q?: string; injuryStatus?: 'fit'|'injured'|'recovering'|'questionable'; minRating?: number; maxRating?: number; recentDays?: number; playedLastMatch?: boolean; hasPerformanceData?: boolean; group?: 'squad' | 'external' }, page = 1, limit = 20): Promise<PaginatedResponse<PlayerWithPerformance>> => {
    // First, get all players
    const playersResponse = await playersAPI.getAll(filters, page, limit);


    // Then, fetch performance data for each player
    const playersWithPerformance: PlayerWithPerformance[] = await Promise.all(
      playersResponse.data.map(async (player, index) => {
        try {
          const response = await api.get(`/players/${player.id}/performance-reports`);
          const reports = response.data.data;

          if (reports && reports.length > 0) {
            const ratings = reports.map((r: PerformanceReport) => r.rating);
            const averageRating = ratings.reduce((sum: number, rating: number) => sum + rating, 0) / ratings.length;
            const latestRating = reports[0]?.rating; // Assuming reports are sorted by date desc

            // Simple trend calculation based on first and last reports
            let performanceTrend: 'improving' | 'declining' | 'stable' = 'stable';
            if (reports.length >= 2) {
              const recentAvg = reports.slice(0, Math.ceil(reports.length / 2)).reduce((sum: number, r: PerformanceReport) => sum + r.rating, 0) / Math.ceil(reports.length / 2);
              const earlierAvg = reports.slice(Math.ceil(reports.length / 2)).reduce((sum: number, r: PerformanceReport) => sum + r.rating, 0) / Math.floor(reports.length / 2);
              if (recentAvg > earlierAvg + 5) performanceTrend = 'improving';
              else if (recentAvg < earlierAvg - 5) performanceTrend = 'declining';
            }

            return {
              ...player,
              averageRating,
              latestRating,
              performanceTrend,
              totalReports: reports.length
            };
          } else {
            return {
              ...player,
              averageRating: undefined,
              latestRating: undefined,
              performanceTrend: undefined,
              totalReports: 0
            };
          }
        } catch (error) {
          // If performance data fetch fails, return player without performance data
          return {
            ...player,
            averageRating: undefined,
            latestRating: undefined,
            performanceTrend: undefined,
            totalReports: 0
          };
        }
      })
    );

    return {
      ...playersResponse,
      data: playersWithPerformance
    };
  },

  getById: async (id: string): Promise<ApiResponse<Player>> => {
    const response = await api.get(`/players/${id}`);
    return response.data;
  },

  create: async (player: PlayerForm): Promise<ApiResponse<Player>> => {
    // If a file is provided, send multipart/form-data
    if (player.photoFile) {
      const form = new FormData();
      form.append('name', player.name);
      form.append('age', String(player.age));
      form.append('position', player.position);
      if (player.team) form.append('team', player.team);
      if (player.photoUrl) form.append('photo_url', player.photoUrl);
      form.append('photo', player.photoFile);
      const response = await api.post('/players', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const payload: Record<string, unknown> = { ...player };
    if (payload.photoUrl !== undefined) {
      payload.photo_url = payload.photoUrl;
      delete payload.photoUrl;
    }
    delete payload.photoFile;
    const response = await api.post('/players', payload);
    return response.data;
  },

  update: async (id: string, player: Partial<PlayerForm>): Promise<ApiResponse<Player>> => {
    // If a file is provided, send multipart/form-data
    if (player.photoFile) {
      const form = new FormData();
      if (player.name !== undefined) form.append('name', player.name);
      if (player.age !== undefined) form.append('age', String(player.age));
      if (player.position !== undefined) form.append('position', player.position);
      if (player.team !== undefined) form.append('team', player.team);
      if (player.photoUrl !== undefined && player.photoUrl) form.append('photo_url', player.photoUrl);
      form.append('photo', player.photoFile);
      const response = await api.put(`/players/${id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const payload: Record<string, unknown> = { ...player };
    if (payload.photoUrl !== undefined) {
      payload.photo_url = payload.photoUrl;
      delete payload.photoUrl;
    }
    delete payload.photoFile;
    const response = await api.put(`/players/${id}`, payload);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/players/${id}`);
  },

  moveWorkspace: async (playerId: string, workspaceId: string): Promise<Player> => {
    const response = await api.put(`/players/${playerId}/workspace`, { workspaceId });
    return response.data.data;
  },
};
